import React, { FunctionComponent, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Text from "../../../components/Text";
import Column from "../../../components/Column";
import Table from "../../../components/Table";
import { useNavigate, useLocation } from "react-router-dom";
import { styles } from "../../../styles/themes/style";
import { AppRoutes } from "../../../interfaces/store/appStore";
import { useAppHandlers } from "../../../services/actions/app/useAppHandlers";
import { useHubspotHandlers } from "../../../services/actions/hubspot/useHubspotHandlers";
import Row from "../../../components/Row";
import Button from "../../../components/Button";
import { calculateColumnWidth } from "../../../utils";
import useMediaQuery from "../../../components/useMediaQuery";
import Select from "../../../components/select/Select";
import TooltipWrapper from "../../../components/TooltipWrapper";

interface IMappingHubSpotProps { }

const MappingHubSpot: FunctionComponent<IMappingHubSpotProps> = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { exportParams } = location.state || {}
    const tableRef = useRef<HTMLDivElement>(null)
    const hasValidatedRef = useRef<boolean>(false)
    const isSmall = useMediaQuery("(max-width: 768px)")

    const { showSuccessToast, showErrorToast } = useAppHandlers()
    const { getContactPropertiesHandler, saveFieldMappingsHandler, exportToHubspotHandler } = useHubspotHandlers()

    const [leadDeltaFields, setLeadDeltaFields] = useState<any[]>([])
    const [hubspotProperties, setHubspotProperties] = useState<any[]>([])
    const [selectedMappings, setSelectedMappings] = useState<any>({})
    const [loading, setLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [fieldErrors, setFieldErrors] = useState<any>({})

    // Required mapping: linkedinUrl must always map to hs_linkedin
    const REQUIRED_LINKEDIN_MAPPING = {
        ldField: 'linkedinUrl',
        hubspotProperty: 'hs_linkedin_url'
    }

    const [columns, setColumns] = useState([
        { header: 'LeadDelta Field', key: ['ldField'], width: '200px', show: true, showSmall: true },
        { header: 'Description', key: ['description'], show: true, showSmall: true },
        { header: 'HubSpot Property', key: ['mapping'], width: '300px', show: true, showSmall: true }
    ])

    /**
     * Check if HubSpot field type is compatible with LeadDelta field type
     */
    const isTypeCompatible = (ldFieldType: string, hubspotType: string, hubspotFieldType?: string): boolean => {
        // Exact match
        if (ldFieldType === hubspotType) return true

        // Type compatibility mappings
        const typeMap: any = {
            'string': ['string', 'text'],
            'email': ['string', 'text', 'email'],
            'phoneNumber': ['string', 'text', 'phone'],
            'url': ['string', 'text', 'url'],
            'text': ['string', 'text'],
            'number': ['number'],
            'date': ['date', 'datetime'],
            'timestamp': ['date', 'datetime'],
            'boolean': ['bool', 'boolean'],
            'array': ['enumeration', 'string', 'text'], // Arrays can map to multi-select enumerations or be joined as strings
        }

        const compatibleTypes = typeMap[ldFieldType] || []
        
        // Check if HubSpot type is in compatible types
        if (compatibleTypes.includes(hubspotType)) return true
        
        // Special case: enumeration can sometimes work for strings
        if (ldFieldType === 'string' && hubspotType === 'enumeration') return true
        
        return false
    }

    useEffect(() => {
        fetchHubSpotProperties()
    }, [])

    useEffect(() => {
        const offsetWidth = tableRef.current?.offsetWidth
        const adjustedColumns = calculateColumnWidth(offsetWidth, columns, isSmall)
        setColumns(adjustedColumns)
    }, [isSmall])

    // Validate existing mappings when data is loaded (only once)
    useEffect(() => {
        if (!hasValidatedRef.current && leadDeltaFields.length > 0 && hubspotProperties.length > 0) {
            const currentMappings = selectedMappings
            if (Object.keys(currentMappings).length === 0) return
            
            hasValidatedRef.current = true
            const errors: any = {}
            
            Object.entries(currentMappings).forEach(([ldFieldValue, hubspotPropName]: [string, any]) => {
                if (!hubspotPropName) return // Skip empty mappings
                
                const ldField = leadDeltaFields.find(f => f.value === ldFieldValue)
                const hubspotProp = hubspotProperties.find(p => p.name === hubspotPropName)
                
                if (ldField && hubspotProp) {
                    const isCompatible = isTypeCompatible(ldField.type, hubspotProp.type, hubspotProp.fieldType)
                    if (!isCompatible) {
                        // Show error but keep the mapping as is
                        errors[ldFieldValue] = `Type mismatch: "${ldField.label}" has type "${ldField.type}" but "${hubspotProp.label}" has type "${hubspotProp.type}"`
                    }
                }
            })
            
            if (Object.keys(errors).length > 0) {
                setFieldErrors(errors)
                showErrorToast({ 
                    message: `Found ${Object.keys(errors).length} type mismatch(es). Please review and correct the mappings.` 
                })
            }
        }
    }, [leadDeltaFields, hubspotProperties])

    const fetchHubSpotProperties = async () => {
        try {
            setLoading(true)
            hasValidatedRef.current = false // Reset validation flag
            const result = await getContactPropertiesHandler()
            
            if (result) {
                setLeadDeltaFields(result.leadDeltaFields || [])
                setHubspotProperties(result.hubspotFields || [])
                const userSettings = result.userSettings || {}
                
                // Force linkedinUrl to always map to hs_linkedin
                const enforcedMappings = {
                    ...userSettings,
                    [REQUIRED_LINKEDIN_MAPPING.ldField]: REQUIRED_LINKEDIN_MAPPING.hubspotProperty
                }
                
                setSelectedMappings(enforcedMappings)
            }

        } catch (error) {
            console.error('Error fetching HubSpot properties:', error)
            showErrorToast({ message: 'Failed to load HubSpot properties' })
            //navigate(AppRoutes.INTEGRATIONS + '/hubspot')
        } finally {
            setLoading(false)
        }
    }

    const handleMappingChange = (ldField: string, hubspotProperty: string) => {
        // Prevent changing the required linkedinUrl mapping
        if (ldField === REQUIRED_LINKEDIN_MAPPING.ldField) {
            showErrorToast({
                message: 'LinkedIn URL must always be mapped to hs_linkedin. This is crucial for recognizing and mapping contacts to HubSpot to prevent duplicates.'
            })
            return
        }
        
        const ldFieldObj = leadDeltaFields.find(f => f.value === ldField)
        const isNotesField = ldFieldObj?.isNotesField || ldField === 'notes'
        const isTagsField = ldFieldObj?.isTagsField || ldField === 'tags'
        const isCompanyField = ldFieldObj?.isCompanyField || ldField === 'company'
        
        if (!ldFieldObj) {
            // If field not found, just set the mapping
            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: hubspotProperty === 'none' ? null : hubspotProperty
            }))
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
            return
        }

        // If clearing the mapping
        if (hubspotProperty === 'none' || !hubspotProperty) {
            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: null
            }))
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
            return
        }

        // For notes field, skip validation and just save
        if (isNotesField && hubspotProperty === 'notes') {
            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: hubspotProperty
            }))
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
            return
        }

        // For tags field, skip validation and just save
        if (isTagsField && hubspotProperty === 'tags') {
            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: hubspotProperty
            }))
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
            return
        }

        // For company field, skip validation and just save
        if (isCompanyField && hubspotProperty === 'company') {
            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: hubspotProperty
            }))
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
            return
        }

        // Find the selected HubSpot property
        const selectedHubspotProp = hubspotProperties.find(p => p.name === hubspotProperty)                                                                     
        
        if (!selectedHubspotProp) {
            // Property not found, clear error and set mapping
            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: hubspotProperty
            }))
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
            return
        }

        const duplicate = Object.entries(selectedMappings).find(([key, value]) => key !== ldField && value === hubspotProperty)

        if (duplicate) {
            const [conflictingFieldKey] = duplicate
            const conflictingField = leadDeltaFields.find(f => f.value === conflictingFieldKey)
            const conflictLabel = conflictingField?.label || 'one field'

            setSelectedMappings((prev: any) => ({
                ...prev,
                [ldField]: hubspotProperty
            }))

            setFieldErrors((prev: any) => ({
                ...prev,
                [ldField]: `HubSpot property "${selectedHubspotProp.label}" is already mapped to "${conflictLabel}".`
            }))

            showErrorToast({
                message: `HubSpot property "${selectedHubspotProp.label}" is already mapped to "${conflictLabel}".`
            })
            return
        }
 
        // Check type compatibility
        const isCompatible = isTypeCompatible(ldFieldObj.type, selectedHubspotProp.type, selectedHubspotProp.fieldType)                                          
 
        // Always keep the user's selection
        setSelectedMappings((prev: any) => ({
            ...prev,
            [ldField]: hubspotProperty
        }))
        
        if (!isCompatible) {
            // Type mismatch - show error but keep user's selection
            setFieldErrors((prev: any) => ({
                ...prev,
                [ldField]: `Type mismatch: "${ldFieldObj.label}" has type "${ldFieldObj.type}" but "${selectedHubspotProp.label}" has type "${selectedHubspotProp.type}"`
            }))
            showErrorToast({ 
                message: `Type mismatch: "${ldFieldObj.label}" (${ldFieldObj.type}) cannot be mapped to "${selectedHubspotProp.label}" (${selectedHubspotProp.type})` 
            })
        } else {
            // Types are compatible - clear any previous errors
            setFieldErrors((prev: any) => {
                const newErrors = { ...prev }
                delete newErrors[ldField]
                return newErrors
            })
        }
    }

    const handleSaveMappings = async () => {
        try {
            setIsSaving(true)
            
            // Ensure linkedinUrl is always mapped to hs_linkedin
            const mappingsToSave = {
                ...selectedMappings,
                [REQUIRED_LINKEDIN_MAPPING.ldField]: REQUIRED_LINKEDIN_MAPPING.hubspotProperty
            }
            
            // First, save the field mappings
            const result = await saveFieldMappingsHandler(mappingsToSave)
            
            if (result) {
                // If we came from export modal, trigger the export
                if (exportParams) {
                    try {
                        await exportToHubspotHandler(exportParams)
                        showSuccessToast({ message: 'Field mappings saved and contacts queued for export to HubSpot!' })
                        navigate(AppRoutes.CONNECTIONS) // Navigate back to connections page
                    } catch (exportError) {
                        console.error('Error exporting to HubSpot:', exportError)
                        showErrorToast({ message: 'Mappings saved but export failed' })
                        navigate(AppRoutes.INTEGRATIONS + '/hubspot')
                    }
                } else {
                    showSuccessToast({ message: 'Field mappings saved successfully!' })
                    navigate(AppRoutes.INTEGRATIONS + '/hubspot')
                }
            }
        } catch (error) {
            console.error('Error saving mappings:', error)
            showErrorToast({ message: 'Failed to save mappings' })
        } finally {
            setIsSaving(false)
        }
    }

    const tableData = leadDeltaFields?.map(ldField => {
        const isLinkedInUrlField = ldField.value === REQUIRED_LINKEDIN_MAPPING.ldField
        const isNotesField = ldField.isNotesField || ldField.value === 'notes'
        const isTagsField = ldField.isTagsField || ldField.value === 'tags'
        const isCompanyField = ldField.isCompanyField || ldField.value === 'company'
        
        // For notes/tags/company fields, use custom options instead of HubSpot properties
        const selectOptions = isNotesField
            ? [
                { value: null, label: '-- Do not map --' },
                { value: 'notes', label: 'Notes' }
              ]
            : isTagsField
            ? [
                { value: null, label: '-- Do not map --' },
                { value: 'tags', label: 'Tags' }
              ]
            : isCompanyField
            ? [
                { value: null, label: '-- Do not map --' },
                { value: 'company', label: 'Company' }
              ]
            : [
                { value: null, label: '-- Do not map --' },
                ...hubspotProperties.map(prop => ({
                    value: prop.name,
                    label: prop.label
                }))
              ]
        
        // For notes/tags/company fields, find the value in custom options
        const selectValue = isNotesField
            ? (selectedMappings[ldField.value] === 'notes' ? { value: 'notes', label: 'Notes' } : null)
            : isTagsField
            ? (selectedMappings[ldField.value] === 'tags' ? { value: 'tags', label: 'Tags' } : null)
            : isCompanyField
            ? (selectedMappings[ldField.value] === 'company' ? { value: 'company', label: 'Company' } : null)
            : (hubspotProperties.find(p => p.name === selectedMappings[ldField.value]) ? {
                value: selectedMappings[ldField.value],
                label: hubspotProperties.find(p => p.name === selectedMappings[ldField.value])?.label
              } : null)
        
        return {
            ldField: <Text $bold>{ldField.label}</Text>,
            description: <Text $lighter>{ldField.description}</Text>,
            mapping: (
                <MappingWrapper>
                    <TooltipWrapper tooltipText={
                        isLinkedInUrlField 
                            ? "Required for the correct contact recognition" 
                            : isNotesField
                                ? "Map to Notes to sync connection notes to HubSpot"
                                : isTagsField
                                    ? "Map to Tags to sync connection tags to HubSpot"
                                    : isCompanyField
                                        ? "Map to Company to create/update company in HubSpot and associate with contact"
                                        : "Please choose a Hubspot Contact Property"
                    }>
                        <SelectWrapper>
                            <Select
                                value={selectValue}
                                options={selectOptions}
                                onChange={(option: any) => handleMappingChange(ldField.value, option?.value || 'none')}
                                placeholder={isNotesField ? "Select Notes mapping" : isTagsField ? "Select Tags mapping" : isCompanyField ? "Select Company mapping" : "Select HubSpot property"}
                                isClearable={!isLinkedInUrlField}
                                isDisabled={isLinkedInUrlField}
                            />
                        </SelectWrapper>
                    </TooltipWrapper>
                    {fieldErrors[ldField.value] && !isLinkedInUrlField && (
                        <ErrorText>{fieldErrors[ldField.value]}</ErrorText>
                    )}
                </MappingWrapper>
            )
        }
    })

    return (
        <Container>
            <Header>
                <Column gap='8px'>
                    <Text $heading3>HubSpot Field Mapping</Text>
                    <Text $lighter>
                        {exportParams 
                            ? 'Map your LeadDelta fields to HubSpot contact properties before exporting.'                                                       
                            : 'Map your LeadDelta fields to HubSpot contact properties. This determines how your contact data will be synced to HubSpot.'}      
                    </Text>
                    {exportParams && (
                        <>
                            <Text $bold>
                                {exportParams.exportAll
                                    ? 'Exporting all contacts'
                                    : (() => {
                                        const count = exportParams.connectionIds?.length ?? 0;
                                        return `Exporting ${count} selected contact${count === 1 ? '' : 's'}`;
                                    })()}                                        
                            </Text>
                            {exportParams.exportType && exportParams.exportType !== 'contacts' && (
                                <Text $lighter>
                                    Export type: {
                                        exportParams.exportType === 'list' ? 'Add to HubSpot List' :
                                        exportParams.exportType === 'workflow' ? 'Enroll in HubSpot Workflow' :
                                        exportParams.exportType === 'deals' ? 'Create HubSpot Deals' :
                                        'Export to HubSpot'
                                    }
                                </Text>
                            )}
                        </>
                    )}
                </Column>
            </Header>

            <Content ref={tableRef}>
                <Table
                    pageLoading={loading ? 10 : undefined}
                    columns={columns}
                    data={tableData || []}
                    noDataPage={
                        <Column gap='12px' alignItems>
                            <Text $heading4>No Field Mappings</Text>
                            <Text $lighter>Unable to load HubSpot properties</Text>
                        </Column>
                    }
                />
            </Content>

            <Footer>
                <Row gap='12px'>
                    <Button 
                        $type='secondary' 
                        $mediumButton
                        onClickHandler={() => navigate(AppRoutes.INTEGRATIONS + '/hubspot')}                                                                    
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button 
                        $type='blue' 
                        $bigButton
                        onClickHandler={handleSaveMappings}
                        disabled={isSaving || Object.keys(fieldErrors).length > 0}                                                                              
                        isLoading={isSaving}
                    >
                        {exportParams 
                            ? (isSaving ? 'Saving & Exporting...' : 'Save & Export')                                                                 
                            : 'Save Mappings'}
                    </Button>
                </Row>
            </Footer>
        </Container>
    )
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: ${styles.colors.uiBackground};
`

const Header = styled.div`
    padding: 24px;
    border-bottom: 1px solid ${({ theme }) => theme.quinaryColor};
    background: white;
`

const Content = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 24px;
`

const Footer = styled.div`
    padding: 16px 24px;
    border-top: 1px solid ${({ theme }) => theme.quinaryColor};
    background: white;
    display: flex;
    justify-content: flex-end;
`

const SelectWrapper = styled.div`
    width: 100%;
    position: relative;
`

const MappingWrapper = styled(Column)`
    width: 100%;
    gap: 4px;
`

const ErrorText = styled(Text)`
    color: ${styles.colors.red600};
    font-size: 12px;
    margin-top: 4px;
`

export default MappingHubSpot

